import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import useAftermarketPurchaseFromQuery from "@modules/purchase/warranty/hooks/useAftermarketPurchaseFromQuery";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef } from "react";
import { gtmPushData, GetBackgroundStyle } from "@talladega/helpers";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import validateRequiredRatesCallFields from "@modules/purchase/warranty/helpers/validateRequiredRatesCallFields";
import { WarrantySelectCoverageLayout } from "@modules/cms/types/WarrantySelectCoverageLayout";
import { useWarrantyQuotePageContext } from "@modules/purchase/WarrantyQuotePageContext";
import DealSegment from "@common/enums/DealSegment";
import { ExtendedWarrantyPremiumAutoCanadaDigital } from "@modules/purchase/warranty/types/IAProgram";
import WarrantySelectCoverageCards from "./WarrantySelectCoverageCards";
import WarrantySelectCoverageHeadingWithConfiguration from "./WarrantySelectCoverageHeadingWithConfiguration";
import WarrantySelectCoverageHeading from "./WarrantySelectCoverageHeading";
import { CoverageSearchBy } from "../AvailableCoveragesModal";

interface WarrantySelectCoverageProps {
  layout: WarrantySelectCoverageLayout;
}

const WarrantySelectCoverage = ({ layout }: WarrantySelectCoverageProps) => {
  const { showHeading, background } = layout;
  const router = useRouter();
  const { query } = router;
  const viewMode = query.view === "quote" ? "quote" : undefined;
  const warrantyOptionsSectionRef = useRef<HTMLDivElement>(null);

  const aftermarketPurchaseFromQuery = useAftermarketPurchaseFromQuery();
  const { purchase: purchaseResponse, isLoading: isPurchaseLoading } =
    useAftermarketPurchase();
  const purchase = tryGetActiveAftermarketPurchase(purchaseResponse);

  const rateDiscountValue =
    purchase?.appliedPromoCode?.discountAmount ||
    purchase?.products[0]?.discount;

  const {
    setShowCoveragesModal,
    setCoveragesModalSearchType,
    onSelectCoverage,
    isSubmitted,
    defaultRates,
    dealSegment,
  } = useWarrantyQuotePageContext();

  const handleOpenCoveragesModal = (
    element: string,
    searchType?: CoverageSearchBy
  ) => {
    gtmPushData({
      event: "ManualQuoteLookup",
      element,
      description: `${element} Button Clicked`,
      source: purchase?.dealSource || "",
    });

    setShowCoveragesModal(true);
    setCoveragesModalSearchType(searchType);
  };

  const {
    vin,
    mileage,
    year,
    make,
    model,
    coverImage: coverImageParam,
  } = aftermarketPurchaseFromQuery;

  const scrollToWarrantyOptionsSection = () => {
    warrantyOptionsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleCustomEvent = () => {
      scrollToWarrantyOptionsSection();
    };
    if (showHeading) {
      // Add an event listener for the custom event, if the layout has a heading
      window.addEventListener(
        "scrollToWarrantyOptionsSection",
        handleCustomEvent
      );
    }

    return () => {
      if (showHeading) {
        window.removeEventListener(
          "scrollToWarrantyOptionsSection",
          handleCustomEvent
        );
      }
    };
  }, [showHeading]);

  const getVehicleCoverImage = (): string => {
    if (!purchase) {
      return coverImageParam ?? "";
    }

    const existingVehicle = purchase.vehicle;
    const savedCoverImage = existingVehicle?.image;
    const isVinMatch = existingVehicle?.vin === vin;
    const areRequiredFieldsAvailable = validateRequiredRatesCallFields({
      vin,
      year: year ? year.toString() : "",
      make,
      model,
      odometer: mileage,
    });

    if (!areRequiredFieldsAvailable) {
      return savedCoverImage ?? "";
    }

    return isVinMatch ? savedCoverImage ?? "" : coverImageParam ?? "";
  };

  const isPremiumCoverageWithoutSuperiorWrap =
    defaultRates?.data?.productDetails.programId ===
      ExtendedWarrantyPremiumAutoCanadaDigital.id &&
    defaultRates?.data?.coverages.every((x) => x.type !== "SW");

  const shouldDisplayWarrantySelectCards = useMemo(() => {
    return isPremiumCoverageWithoutSuperiorWrap;
  }, [isPremiumCoverageWithoutSuperiorWrap]);

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return showHeading ? (
    <div
      id="cms-warranty-select-coverage-section"
      ref={
        dealSegment !== DealSegment.InstantQuote && !isPurchaseLoading
          ? warrantyOptionsSectionRef
          : null
      }
      style={
        defaultRates?.isSuccess && shouldDisplayWarrantySelectCards
          ? GetBackgroundStyle(background, baseDomain)
          : { backgroundColor: background.backgroundColor ?? "transparent" }
      }
    >
      {/* Default to the original heading if displaying warranty select cards or if the rates are not available */}
      {shouldDisplayWarrantySelectCards || !defaultRates?.isSuccess ? (
        <WarrantySelectCoverageHeading
          isRatesAvailable={defaultRates?.isSuccess}
          vehicleCoverImage={getVehicleCoverImage()}
          vehicleDisplayDetails={defaultRates?.data?.vehicleDisplayDetails}
          onLookUpWarrantyBtnClick={handleOpenCoveragesModal}
        />
      ) : (
        <div>
          <WarrantySelectCoverageHeadingWithConfiguration
            extWarrantyRates={defaultRates.data}
            rateDiscountValue={rateDiscountValue}
            onQuoteDifferentVehicle={(element: string) =>
              handleOpenCoveragesModal(element)
            }
            onSelectCoverage={onSelectCoverage}
          />
        </div>
      )}

      {defaultRates?.isSuccess &&
        dealSegment === DealSegment.InstantQuote &&
        !isPurchaseLoading && (
          <div className="no-scrollbar relative overflow-visible bg-cover bg-center">
            <div className="mx-auto max-w-page px-4 pb-6 lg:px-5 lg:pt-2">
              <div
                className="text-center lg:pt-8"
                ref={warrantyOptionsSectionRef}
              >
                {shouldDisplayWarrantySelectCards && (
                  <WarrantySelectCoverageCards
                    ratesResponse={defaultRates?.data}
                    onSelectCoverage={onSelectCoverage}
                    disabled={isSubmitted}
                    viewMode={viewMode}
                    rateDiscountValue={rateDiscountValue}
                    purchase={purchase}
                    isPremiumCoverageWithoutSuperiorWrap={
                      isPremiumCoverageWithoutSuperiorWrap
                    }
                  />
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  ) : (
    <div />
  );
};

export default WarrantySelectCoverage;
